import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HardDrive, Package } from 'lucide-react';
import { licensingApi, isPlatformAdminRequiredError } from '@/api/licensing';
import { migrationApi, type MigrationLogRow } from '@/api/migration';
import { orgsApi } from '@/api/orgs';
import { getWhatsappDeviceMonthlyBillingForOrg } from '@/api/whatsapp';
import { supabase } from '@/lib/supabase';
import { Button, Loading, EmptyState, Modal, Input, Badge } from '@/components/ui';
import { LIMIT_KEYS, LIMIT_LABELS, type LimitKey } from '@/types/licensing';
import { formatNIS } from '@/utils/currency';
import { formatDate } from '@/utils/date';
import SubscriptionEditModal from './SubscriptionEditModal';
import CommercialAgreementPanel from './CommercialAgreementPanel';
import { couponsApi } from '@/api/coupons';
import toast from 'react-hot-toast';

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active:    { bg: '#22c55e20', text: '#4ade80' },
  trial:     { bg: '#f59e0b20', text: '#fbbf24' },
  suspended: { bg: '#ef444420', text: '#f87171' },
  cancelled: { bg: '#6b728020', text: '#9ca3af' },
};

interface Props { orgId: string }

function getUsageVariant(used: number, effective: number): 'success' | 'warning' | 'danger' | 'info' {
  if (effective === -1) return 'success';
  if (effective <= 0) return used > 0 ? 'danger' : 'info';

  const ratio = used / effective;
  if (ratio > 0.9) return 'danger';
  if (ratio > 0.7) return 'warning';
  return 'success';
}

function formatUsageValue(key: LimitKey, used: number, effective: number): string {
  const usedLabel = key === 'storage_gb' ? `${used}GB` : String(used);
  if (effective === -1) return `${usedLabel}/Unlimited`;
  const effectiveLabel = key === 'storage_gb' ? `${effective}GB` : String(effective);
  return `${usedLabel}/${effectiveLabel}`;
}

function UsageStrip({ orgId }: Props) {
  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['org-effective-limits', orgId],
    queryFn: () => licensingApi.getEffectiveLimits(orgId),
    staleTime: 15_000,
    retry: false,
  });

  if (isLoading) {
    return <Loading text="Loading usage limits..." />;
  }

  if (isError && isPlatformAdminRequiredError(error)) {
    return (
      <div className="card p-4">
        <EmptyState
          icon={<Package className="h-8 w-8" />}
          title="Platform admin required"
          description="Usage limits for other organizations require is_platform_admin() or is_super_admin()."
        />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink-primary">Usage vs limit</h3>
          <p className="text-xs text-ink-secondary">Plan limits plus active add-ons</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {LIMIT_KEYS.map((key) => {
          const snapshot = data[key];
          return (
            <Badge
              key={key}
              variant={getUsageVariant(snapshot.used, snapshot.effective)}
              className="gap-1.5 px-2.5 py-1 text-[11px]"
            >
              <span className="font-semibold">{LIMIT_LABELS[key].label}</span>
              <span className="num">{formatUsageValue(key, snapshot.used, snapshot.effective)}</span>
            </Badge>
          );
        })}
      </div>
    </div>
  );
}

function includedDevicesFromPlan(features: Record<string, unknown> | null | undefined): number | null {
  if (!features || typeof features !== 'object') return null;
  const n = Number((features as { whatsapp_devices?: unknown }).whatsapp_devices);
  return Number.isFinite(n) ? n : null;
}

function extraDeviceRateFromPlan(features: Record<string, unknown> | null | undefined): number {
  if (!features || typeof features !== 'object') return 0;
  const n = Number((features as { extra_device_agorot?: unknown }).extra_device_agorot);
  return Number.isFinite(n) ? n : 0;
}

function renderDeviceLine(
  showDeviceQuota: boolean,
  subStatus: string,
  planFeatures: Record<string, unknown> | undefined,
  deviceCount: number,
  billing: { totalPriceAgorot: number; extraBilledCount: number; extraBilledSumAgorot: number }
) {
  if (!showDeviceQuota) return null;

  const included = includedDevicesFromPlan(planFeatures);
  const isTrial = subStatus === 'trial';

  if (isTrial) {
    return (
      <p className="text-xs text-ink-secondary mt-1">
        Devices:{' '}
        <span className="num font-medium text-ink-primary">
          {deviceCount} billed (trial — no free quota) • {formatNIS(billing.totalPriceAgorot)}/mo
        </span>
      </p>
    );
  }

  const overPlan =
    included !== null ? Math.max(0, deviceCount - included) : 0;
  const planExtra = extraDeviceRateFromPlan(planFeatures);

  let extraCount = billing.extraBilledCount;
  let extraSum = billing.extraBilledSumAgorot;
  if (extraCount === 0 && extraSum === 0 && overPlan > 0 && planExtra > 0) {
    extraCount = overPlan;
    extraSum = Math.round(overPlan * planExtra);
  }

  return (
    <p className="text-xs text-ink-secondary mt-1">
      Devices:{' '}
      <span className="num font-medium text-ink-primary">
        {deviceCount} / {included !== null ? included : '—'} included
        {extraCount > 0 && extraSum > 0 ? (
          <>
            {' '}
            • {extraCount} extra @ {formatNIS(extraSum)}/mo
          </>
        ) : null}
      </span>
    </p>
  );
}

function StorageBillingCard({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [priceNis, setPriceNis] = useState('');
  const [freeGb, setFreeGb] = useState('5');
  const [usedGb, setUsedGb] = useState('0');

  const { data: row, isLoading } = useQuery({
    queryKey: ['org-storage-billing', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('storage_price_per_gb_agorot, storage_free_gb, storage_used_gb')
        .eq('id', orgId)
        .single();
      if (error) throw error;
      return data as {
        storage_price_per_gb_agorot: number | null;
        storage_free_gb: number | null;
        storage_used_gb: string | number | null;
      };
    },
    staleTime: 30_000,
  });

  const priceAgorot = row?.storage_price_per_gb_agorot ?? 0;
  const free = row?.storage_free_gb ?? 5;
  const used = Number(row?.storage_used_gb ?? 0);

  const billableGb = useMemo(() => Math.max(0, used - free), [used, free]);
  const billAgorot = useMemo(
    () => Math.round(billableGb * priceAgorot),
    [billableGb, priceAgorot]
  );

  useEffect(() => {
    if (!editOpen || !row) return;
    setPriceNis(String((priceAgorot / 100).toFixed(2)));
    setFreeGb(String(free));
    setUsedGb(String(used));
  }, [editOpen, row, priceAgorot, free, used]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const pa = Math.round(Number(priceNis) * 100);
      const fg = Math.max(0, Math.floor(Number(freeGb)));
      const ug = Number(usedGb);
      if (!Number.isFinite(pa) || pa < 0) throw new Error('Invalid price per GB');
      if (!Number.isFinite(fg)) throw new Error('Invalid free GB');
      if (!Number.isFinite(ug) || ug < 0) throw new Error('Invalid used GB');
      await orgsApi.updateStorageBilling(orgId, {
        pricePerGbAgorot: pa,
        freeGb: fg,
        usedGb: ug,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['org-storage-billing', orgId] });
      await qc.invalidateQueries({ queryKey: ['admin-org', orgId] });
      setEditOpen(false);
    },
  });

  if (isLoading) {
    return (
      <div className="card p-5">
        <Loading text="Loading storage…" />
      </div>
    );
  }

  return (
    <>
      <div className="card p-5 flex items-start gap-4">
        <div className="h-10 w-10 rounded-lg bg-sky-500/10 flex items-center justify-center text-sky-400 flex-shrink-0">
          <HardDrive className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className="font-medium text-ink-primary text-sm">Storage (R2)</p>
            <Button type="button" variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          </div>
          <p className="text-xs text-ink-secondary num">
            {used.toFixed(2)} GB used
          </p>
          <p className="text-xs text-ink-muted">
            {free} GB free • {formatNIS(priceAgorot)}/GB after
          </p>
          <p className="text-xs text-ink-secondary num">
            Billable this month: {billableGb.toFixed(2)} GB = {formatNIS(billAgorot)}/mo
          </p>
        </div>
      </div>

      <Modal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit storage billing (R2)"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setEditOpen(false)} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button
              loading={saveMutation.isPending}
              onClick={() => void saveMutation.mutateAsync()}
            >
              Save
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <Input
            label="Price per GB (₪/mo)"
            type="number"
            min="0"
            step="0.01"
            value={priceNis}
            onChange={(e) => setPriceNis(e.target.value)}
          />
          <Input
            label="Free GB"
            type="number"
            min="0"
            step="1"
            value={freeGb}
            onChange={(e) => setFreeGb(e.target.value)}
          />
          <Input
            label="Used GB (admin override)"
            type="number"
            min="0"
            step="0.01"
            value={usedGb}
            onChange={(e) => setUsedGb(e.target.value)}
          />
          {saveMutation.error ? (
            <p className="text-xs text-danger">{(saveMutation.error as Error).message}</p>
          ) : null}
        </div>
      </Modal>
    </>
  );
}

export default function OrgSubscriptionsTab({ orgId }: Props) {
  const { data: subs, isLoading } = useQuery({
    queryKey: ['admin-org-subs', orgId],
    queryFn: () => licensingApi.getOrgSubscriptions(orgId),
  });

  const { data: waBilling } = useQuery({
    queryKey: ['admin-org-wa-billing', orgId],
    queryFn: () => getWhatsappDeviceMonthlyBillingForOrg(orgId),
    staleTime: 30_000,
  });

  const [editingSubId, setEditingSubId] = useState<string | null>(null);
  const [attachingSubId, setAttachingSubId] = useState<string | null>(null);
  const editingSub = useMemo(
    () => (subs ?? []).find((s) => s.id === editingSubId) ?? null,
    [subs, editingSubId],
  );
  const attachingSub = useMemo(
    () => (subs ?? []).find((s) => s.id === attachingSubId) ?? null,
    [subs, attachingSubId],
  );

  if (isLoading) return <Loading text="Loading subscriptions..." />;

  const billing = waBilling ?? {
    deviceCount: 0,
    totalPriceAgorot: 0,
    extraBilledCount: 0,
    extraBilledSumAgorot: 0,
  };

  return (
    <div className="space-y-4">
      <UsageStrip orgId={orgId} />
      <CommercialAgreementPanel orgId={orgId} />

      {subs?.length ? (
        subs.map((sub) => {
          const sc = STATUS_COLORS[sub.status] ?? STATUS_COLORS.cancelled;
          const systemName = sub.systems?.name ?? sub.system_code;
          const planName = sub.system_license_plans?.name ?? sub.plan_code;
          const showDeviceQuota =
            sub.system_code === 'jumpstart-chat' || sub.system_code === 'whatsapp';
          const planFeatures = sub.system_license_plans && 'features' in sub.system_license_plans
            ? (sub.system_license_plans as { features?: Record<string, unknown> }).features
            : undefined;
          const planPrices = sub.system_license_plans as {
            price_monthly_agorot?: number | null;
            price_yearly_agorot?: number | null;
          } | null;
          const listPriceAgorot = sub.billing_cycle === 'yearly'
            ? planPrices?.price_yearly_agorot
            : planPrices?.price_monthly_agorot;
          const hasListPrice = typeof listPriceAgorot === 'number';
          const negotiatedPriceAgorot = typeof sub.effective_price_agorot === 'number'
            ? sub.effective_price_agorot
            : null;
          const displayPriceAgorot = hasListPrice
            ? negotiatedPriceAgorot ?? listPriceAgorot
            : sub.price_agorot;
          const priceSuffix = sub.billing_cycle === 'monthly'
            ? '/mo'
            : sub.billing_cycle === 'yearly'
              ? '/yr'
              : '';
          const showListPrice = hasListPrice && listPriceAgorot !== displayPriceAgorot;
          const hasDiscount = hasListPrice
            && negotiatedPriceAgorot !== null
            && negotiatedPriceAgorot < listPriceAgorot;
          const showPrice = hasListPrice || displayPriceAgorot > 0;

          return (
            <div key={sub.id} className="card p-5 flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-teal/10 flex items-center justify-center text-teal flex-shrink-0">
                <Package className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-ink-primary text-sm">{systemName}</span>
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                      style={{ background: sc.bg, color: sc.text }}
                    >
                      {sub.status}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setEditingSubId(sub.id)}
                  >
                    Edit
                  </Button>
                </div>

                <p className="text-xs text-ink-secondary mt-0.5">{planName}</p>

                {renderDeviceLine(showDeviceQuota, sub.status, planFeatures, billing.deviceCount, billing)}

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                  {showPrice && (
                    <span className="inline-flex items-center gap-2 text-xs">
                      {showListPrice && (
                        <span className="num text-ink-muted line-through">
                          {formatNIS(listPriceAgorot)}{priceSuffix}
                        </span>
                      )}
                      <span className="num font-semibold text-ink-primary">
                        {formatNIS(displayPriceAgorot)}{priceSuffix}
                      </span>
                      {hasDiscount && (
                        <span
                          className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-400"
                          title={sub.override_reason ?? undefined}
                        >
                          discount
                        </span>
                      )}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setAttachingSubId(sub.id)}
                    className="text-xs px-2 py-0.5 rounded border border-navy-border text-ink-secondary hover:text-gold hover:border-gold"
                  >
                    Attach coupon
                  </button>
                  {(sub as any).coupon_code && (
                    <span className="text-xs text-indigo-300 num">
                      coupon: {(sub as any).coupon_code}
                    </span>
                  )}
                  <span className="text-xs text-ink-muted capitalize">
                    {sub.billing_cycle !== 'none' ? sub.billing_cycle : 'One-time'}
                  </span>
                  {sub.started_at && (
                    <span className="text-xs text-ink-muted num">
                      Started: {formatDate(sub.started_at)}
                    </span>
                  )}
                  {sub.expires_at && sub.status === 'active' && (
                    <span className="text-xs text-ink-muted num">
                      Expires: {formatDate(sub.expires_at)}
                    </span>
                  )}
                </div>

                {sub.notes && <p className="text-xs text-ink-muted mt-1">{sub.notes}</p>}
              </div>
            </div>
          );
        })
      ) : (
        <EmptyState
          icon={<Package className="h-8 w-8" />}
          title="No subscriptions"
          description="No system subscriptions found for this org"
        />
      )}

      <StorageBillingCard orgId={orgId} />

      <MigrationHistoryPanel orgId={orgId} />

      {editingSub && (
        <SubscriptionEditModal
          open={!!editingSub}
          onClose={() => setEditingSubId(null)}
          orgId={orgId}
          sub={editingSub as any}
        />
      )}

      {attachingSub && (
        <AttachCouponModal
          open={!!attachingSub}
          onClose={() => setAttachingSubId(null)}
          orgId={orgId}
          subscriptionId={attachingSub.id}
        />
      )}
    </div>
  );
}

function AttachCouponModal({
  open,
  onClose,
  orgId,
  subscriptionId,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  subscriptionId: string;
}) {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const redeemMut = useMutation({
    mutationFn: async () => {
      const c = code.trim().toUpperCase();
      if (!c) throw new Error('Code required');
      return couponsApi.redeem(c, orgId, subscriptionId);
    },
    onSuccess: async (res) => {
      toast.success(`Coupon applied — new price ${formatNIS(res.new_price_agorot)}`);
      await qc.invalidateQueries({ queryKey: ['admin-org-subs', orgId] });
      await qc.invalidateQueries({ queryKey: ['coupon-redemptions'] });
      setCode('');
      onClose();
    },
    onError: (e: any) => toast.error(e?.message ?? 'Redeem failed'),
  });
  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Attach coupon"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={redeemMut.isPending}>Cancel</Button>
          <Button onClick={() => redeemMut.mutate()} disabled={redeemMut.isPending}>
            {redeemMut.isPending ? 'Redeeming…' : 'Redeem'}
          </Button>
        </>
      }
    >
      <Input
        label="Coupon code"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="LAUNCH50"
      />
      {redeemMut.error ? (
        <p className="text-xs text-red-400 mt-2">{(redeemMut.error as Error).message}</p>
      ) : null}
    </Modal>
  );
}

function MigrationHistoryPanel({ orgId }: { orgId: string }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['migration', 'history', orgId],
    queryFn: () => migrationApi.listHistory(orgId),
    enabled: open,
  });

  const revertMut = useMutation({
    mutationFn: (id: string) => migrationApi.revert(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['migration', 'history', orgId] });
      qc.invalidateQueries({ queryKey: ['org-system-subscriptions', orgId] });
    },
  });

  const canRevert = (row: MigrationLogRow) => {
    if (row.status !== 'applied' || !row.applied_at) return false;
    const ageMs = Date.now() - new Date(row.applied_at).getTime();
    return ageMs < 30 * 24 * 60 * 60 * 1000;
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 text-left text-sm text-white flex justify-between"
      >
        <span>Migration history</span>
        <span className="text-gray-500">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {isLoading && <div className="text-xs text-gray-500">Loading…</div>}
          {!isLoading && (data ?? []).length === 0 && (
            <div className="text-xs text-gray-500">No migrations for this org.</div>
          )}
          {(data ?? []).map((row) => (
            <div key={row.id} className="flex items-center justify-between py-2" style={{ borderTop: '1px solid #2a2d3e' }}>
              <div className="text-xs">
                <div className="text-white">
                  {row.from_plan_code ?? '—'} → {row.to_plan_code}
                </div>
                <div className="text-gray-500">
                  {row.status} · {formatDate(row.created_at)}
                  {row.actor_email ? ` · ${row.actor_email}` : ''}
                </div>
              </div>
              {canRevert(row) && (
                <button
                  onClick={() => {
                    if (confirm('Revert this migration? Prior subscription will be restored.')) {
                      revertMut.mutate(row.id);
                    }
                  }}
                  disabled={revertMut.isPending}
                  className="text-xs px-2 py-1 rounded"
                  style={{ background: '#f59e0b20', color: '#fbbf24' }}
                >
                  Revert
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
